import React from 'react';
import ColorPicker from '../ui/ColorPicker';
import { Plus, Trash2 } from 'lucide-react';
import './MusicPlayerBlockEditor.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5014/api/v1';

const MusicPlayerBlockEditor = ({ block, onUpdate }) => {
  const handleChange = (field, value) => {
    console.log(`🔄 handleChange called: field="${field}", value=`, value);
    const newConfig = {
      ...block.config,
      [field]: value,
    };
    console.log('📦 New config:', newConfig);
    onUpdate(block.id, {
      config: newConfig,
    });
  };

  const handleStyleChange = (field, value) => {
    onUpdate(block.id, {
      styles: {
        ...block.styles,
        [field]: value,
      },
    });
  };

  const handleAddTrack = () => {
    const newTrack = {
      id: Date.now(),
      title: 'New Track',
      artist: 'Artist Name',
      spotifyUrl: '',
      audioUrl: '',
      duration: '3:30',
    };
    handleChange('tracks', [...(block.config.tracks || []), newTrack]);
  };

  const handleUpdateTrack = (index, field, value) => {
    const updatedTracks = [...(block.config.tracks || [])];
    updatedTracks[index] = { ...updatedTracks[index], [field]: value };
    handleChange('tracks', updatedTracks);
  };

  const handleSpotifyUrlKeyPress = (e, index) => {
    if (e.key === 'Enter') {
      const track = block.config.tracks[index];
      if (track.spotifyUrl && track.spotifyUrl.includes('spotify.com/track/')) {
        fetchSpotifyTrackDetails(track.spotifyUrl, index);
      }
    }
  };

  const fetchSpotifyTrackDetails = async (url, index) => {
    console.log('🎵 Fetching Spotify details for:', url);

    // Detect if it's a track or album URL
    const isTrack = url.includes('spotify.com/track/');
    const isAlbum = url.includes('spotify.com/album/');

    if (!isTrack && !isAlbum) {
      alert('❌ Please use a Spotify TRACK or ALBUM URL.\n\nExamples:\nTrack: https://open.spotify.com/track/3n3Ppam7vgaVa1iaRUc9Lp\nAlbum: https://open.spotify.com/album/5w3vUnEpFqhgvTUmTwoBbR');
      return;
    }

    try {
      if (isAlbum) {
        // Fetch all tracks from the album
        const response = await fetch(`${API_BASE}/spotify/album?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📦 Spotify album response:', data);

        if (data.success && data.data) {
          const { tracks, albumArt, albumName, totalTracks } = data.data;

          // Remove the current empty track and add all album tracks
          const currentTracks = [...(block.config.tracks || [])];
          currentTracks.splice(index, 1); // Remove the placeholder track

          // Add all tracks from the album
          const newTracks = tracks.map((track, idx) => ({
            id: Date.now() + idx,
            title: track.name,
            artist: track.artist,
            duration: track.durationFormatted,
            spotifyUrl: track.spotifyUrl,
            audioUrl: '',
          }));

          const updatedTracks = [...currentTracks, ...newTracks];

          // Update config with all tracks
          const newConfig = {
            ...block.config,
            tracks: updatedTracks,
          };

          // Set album art as artwork if not already set
          if (albumArt && !block.config.artworkUrl) {
            newConfig.artworkUrl = albumArt;
          }

          console.log('📦 Final config with album tracks:', newConfig);
          onUpdate(block.id, { config: newConfig });

          console.log(`✅ SUCCESS! Added ${totalTracks} tracks from "${albumName}"`);
          alert(`✅ Album loaded: "${albumName}"\n${totalTracks} tracks added`);
        }
      } else {
        // Fetch single track
        const response = await fetch(`${API_BASE}/spotify/track?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📦 Spotify track response:', data);

        if (data.success && data.data) {
          const { name, artist, durationFormatted, albumArt } = data.data;

          // Get current tracks and preserve ALL fields
          const currentTracks = [...(block.config.tracks || [])];
          const currentTrack = currentTracks[index];

          // Update track with fetched data
          const updatedTrack = {
            ...currentTrack,
            title: name,
            artist: artist,
            duration: durationFormatted,
            spotifyUrl: url,
          };

          currentTracks[index] = updatedTrack;

          // Update config
          const newConfig = {
            ...block.config,
            tracks: currentTracks,
          };

          // Set album art as artwork if not already set
          if (albumArt && !block.config.artworkUrl) {
            newConfig.artworkUrl = albumArt;
          }

          console.log('📦 Final config update:', newConfig);
          onUpdate(block.id, { config: newConfig });

          console.log('✅ SUCCESS! Fetched Spotify track:', name, 'by', artist);
          alert(`✅ Track loaded: ${name} by ${artist}`);
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch Spotify details:', error);
      alert(`❌ Error: ${error.message}`);
    }
  };

  const handleRemoveTrack = (index) => {
    const updatedTracks = (block.config.tracks || []).filter((_, i) => i !== index);
    handleChange('tracks', updatedTracks);
  };

  return (
    <div className="music-player-editor">
      <div className="editor-section">
        <h4 className="editor-section-title">Music Player Settings</h4>

        <div className="editor-field">
          <label>Title</label>
          <input
            type="text"
            value={block.config.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Listen to Preview"
          />
        </div>

        <div className="editor-field">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={block.config.showArtwork !== false}
              onChange={(e) => handleChange('showArtwork', e.target.checked)}
            />
            Show Artwork
          </label>
        </div>

        {block.config.showArtwork !== false && (
          <div className="editor-field">
            <label>Artwork URL</label>
            <input
              type="url"
              value={block.config.artworkUrl || ''}
              onChange={(e) => handleChange('artworkUrl', e.target.value)}
              placeholder="https://example.com/artwork.jpg"
            />
          </div>
        )}
      </div>

      <div className="editor-section">
        <h4 className="editor-section-title">Tracks</h4>

        {(block.config.tracks || []).map((track, index) => (
          <div key={track.id || index} className="track-card">
            <div className="track-header">
              <span className="track-number">Track {index + 1}</span>
              <button
                type="button"
                onClick={() => handleRemoveTrack(index)}
                className="btn-remove"
                title="Remove track"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="editor-field">
              <label>Track Title</label>
              <input
                type="text"
                value={track.title || ''}
                onChange={(e) => handleUpdateTrack(index, 'title', e.target.value)}
                placeholder="Track Title"
              />
            </div>

            <div className="editor-field">
              <label>Artist</label>
              <input
                type="text"
                value={track.artist || ''}
                onChange={(e) => handleUpdateTrack(index, 'artist', e.target.value)}
                placeholder="Artist Name"
              />
            </div>

            <div className="editor-field">
              <label>Spotify Track or Album URL</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="url"
                  value={track.spotifyUrl || ''}
                  onChange={(e) => handleUpdateTrack(index, 'spotifyUrl', e.target.value)}
                  onKeyPress={(e) => handleSpotifyUrlKeyPress(e, index)}
                  placeholder="Track or Album URL"
                  style={{ flex: 1 }}
                />
                {track.spotifyUrl && (
                  <>
                    <button
                      type="button"
                      onClick={() => fetchSpotifyTrackDetails(track.spotifyUrl, index)}
                      className="btn-fetch"
                      title="Fetch track details from Spotify"
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#00D9FF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Fetch Details
                    </button>
                    <button
                      type="button"
                      onClick={() => window.open(track.spotifyUrl, '_blank')}
                      className="btn-preview"
                      title="Preview on Spotify"
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#1DB954',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Preview
                    </button>
                  </>
                )}
              </div>
              <span className="field-hint">💡 Paste track URL to add single track, or album URL to add all tracks from album. Click "Fetch Details".</span>
            </div>

            <div className="editor-field">
              <label>Audio URL (MP3/WAV - Optional)</label>
              <input
                type="url"
                value={track.audioUrl || ''}
                onChange={(e) => handleUpdateTrack(index, 'audioUrl', e.target.value)}
                placeholder="https://example.com/track.mp3"
              />
            </div>

            <div className="editor-field">
              <label>Duration</label>
              <input
                type="text"
                value={track.duration || ''}
                onChange={(e) => handleUpdateTrack(index, 'duration', e.target.value)}
                placeholder="3:30"
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          className="btn-add-track"
          onClick={handleAddTrack}
        >
          <Plus size={16} />
          Add Track
        </button>
      </div>

      <div className="editor-section">
        <h4 className="editor-section-title">Styling</h4>

        <ColorPicker
          label="Background Color"
          color={block.styles.backgroundColor || '#1a1a1a'}
          onChange={(color) => handleStyleChange('backgroundColor', color)}
        />

        <ColorPicker
          label="Primary Color (Player Controls)"
          color={block.config.primaryColor || '#00D9FF'}
          onChange={(color) => handleChange('primaryColor', color)}
        />

        <ColorPicker
          label="Text Color"
          color={block.config.textColor || '#ffffff'}
          onChange={(color) => handleChange('textColor', color)}
        />
      </div>
    </div>
  );
};

export default MusicPlayerBlockEditor;
